/* lp-cart.js — the landing-page cart.
 *
 * One module every landing page shares. It talks to Shopify's Ajax Cart API and
 * paints the design's own drawer; it never opens the theme's. Everything the
 * customer is shown about money comes from Shopify's cart JSON, so the drawer
 * cannot promise a total that checkout then contradicts. The Astro pages worked
 * out totals in JS, which is exactly how a drawer and a checkout drift apart.
 *
 * THE CONTRACT — the design marks up its drawer with data attributes, and this
 * fills them in. Nothing here knows what the drawer looks like.
 *
 *   Buttons
 *     data-lp-add="ID:QTY,ID:QTY"   add these variants in ONE request. JSON works
 *                                   too: '[{"id":1,"quantity":2,"properties":{}}]'
 *     data-lp-mode="replace"        empty the cart first (cart-permalink behaviour)
 *     data-lp-discount="CODE"       apply a discount code after adding
 *     data-lp-open="false"          add without opening the drawer
 *     data-lp-open-drawer           just open it
 *
 *   Drawer
 *     data-lp-drawer                the drawer root; toggled via `hidden`
 *     data-lp-dialog                what receives focus when it opens
 *     data-lp-close                 closes it (a backdrop, an X)
 *     data-lp-checkout              its href is set to the market's /checkout
 *
 *   Cart values (text, money formatted like the storefront)
 *     data-lp-cart="count|subtotal|total|original|discount"
 *     data-lp-show="empty|not-empty|discounted|tier-reached|tier-pending|busy|error"
 *     data-lp-error                 the last error message
 *
 *   Lines — a container holding one <template> for a line
 *     data-lp-lines
 *       data-lp-line="title|variant|qty|price|compare|unit|discounts"
 *       data-lp-line-img            <img>; src set from the line image
 *       data-lp-line-show="discounted"
 *       data-lp-action="inc|dec|remove"
 *
 *   Cart-level discounts — a container holding one <template>
 *     data-lp-discounts
 *       data-lp-discount-field="title|amount"
 *
 *   Discount tiers (see lp-cart-config.liquid for where thresholds come from)
 *     data-lp-tier="remaining|next|reached"
 *     data-lp-tier-progress         gets --lp-progress: 0..1 and aria-valuenow
 *
 * Clicks are read with composedPath(), not closest(): the design bundles render
 * inside shadow roots, a click in one retargets to its host, and closest() would
 * find nothing. That exact bug once sent every shop click out unattributed.
 */
(() => {
  if (window.LPCart) return;

  const root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/").replace(/\/?$/, "/");
  const url = (p) => root + p;

  const readConfig = () => {
    const el = document.getElementById("lp-cart-config");
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch { return {}; }
  };
  const config = readConfig();

  /* ---------- money ------------------------------------------------------
   * Formatted like the storefront, by copying the storefront. The config
   * carries `{{ 1234567 | money }}` rendered for the visitor's market, e.g.
   * "12.345,67 zł" or "12 346 Ft"; reading the separators, the decimals and the
   * symbol's position back out of that sample reproduces the `money` filter
   * for every other amount without shipping a table of currency formats. */
  const formatterFromSample = (sample) => {
    const m = /\d[\d.,'\s  ]*\d/.exec(sample || "");
    if (!m) return null;
    const run = m[0];
    const digits = run.replace(/\D/g, "");
    if (digits !== "1234567" && digits !== "12346" && digits !== "12345") return null;
    const decimals = digits.length === 7 ? 2 : 0;
    // The separators in order: "12" <thousands> "345" <decimal> "67". Either may
    // be absent — a currency without grouping, or without minor units.
    const separators = run.match(/\D+/g) || [];
    const thousands = separators.length > (decimals ? 1 : 0) ? separators[0] : "";
    const decimal = decimals ? separators[separators.length - 1] : "";
    const before = sample.slice(0, m.index);
    const after = sample.slice(m.index + run.length);
    return (cents) => {
      const negative = cents < 0;
      const fixed = (Math.abs(cents) / 100).toFixed(decimals);
      const [int, frac] = fixed.split(".");
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (negative ? "-" : "") + before + grouped + (frac ? decimal + frac : "") + after;
    };
  };
  const intlFormatter = (currency) => {
    try {
      const f = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: "currency", currency });
      return (cents) => f.format(cents / 100);
    } catch {
      return (cents) => (cents / 100).toFixed(2);
    }
  };
  let money = formatterFromSample(config.moneySample) || intlFormatter(config.currency || "EUR");

  /* ---------- tiers ------------------------------------------------------
   * Shopify reports a discount once it applies and says nothing about the next
   * one, so "add 40 € more for 50 % off" needs the thresholds from somewhere
   * else. They come from the config, and the behaviour gate checks that what
   * the drawer promises is what the cart then does. */
  const tiers = (Array.isArray(config.tiers) ? config.tiers : [])
    .filter((t) => t && Number.isFinite(Number(t.threshold)))
    .map((t) => ({ ...t, threshold: Number(t.threshold), basis: t.basis || "original_total_price" }))
    .sort((a, b) => a.threshold - b.threshold);

  const tierState = (cart) => {
    if (!tiers.length || !cart) return null;
    const value = (t) => (t.basis === "item_count" ? cart.item_count : cart[t.basis] ?? cart.original_total_price);
    const reached = tiers.filter((t) => value(t) >= t.threshold);
    const next = tiers.find((t) => value(t) < t.threshold) || null;
    const current = reached[reached.length - 1] || null;
    const progress = next ? Math.max(0, Math.min(1, value(next) / next.threshold)) : 1;
    return {
      current, next, progress,
      remaining: next ? next.threshold - value(next) : 0,
      remainingIsCount: next ? next.basis === "item_count" : false,
    };
  };

  /* ---------- requests ---------------------------------------------------
   * Serialised. Two quick clicks used to be two concurrent writes to one cart,
   * and the second could read the cart before the first had landed — the sort
   * of race that loses a product out of five. One queue, one write at a time. */
  let chain = Promise.resolve();
  const enqueue = (fn) => {
    const run = chain.then(fn);
    chain = run.catch(() => {});
    return run;
  };

  const request = async (path, body) => {
    const res = await fetch(url(path), {
      method: body ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
      credentials: "same-origin",
    });
    let data = null;
    try { data = await res.json(); } catch { /* an empty body is still a response */ }
    if (!res.ok) {
      const error = new Error((data && (data.description || data.message)) || `Cart request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return data;
  };

  const parseItems = (spec) => {
    const text = String(spec || "").trim();
    if (!text) return [];
    if (text.startsWith("[")) {
      return JSON.parse(text).map((i) => ({ ...i, id: Number(i.id), quantity: Number(i.quantity || 1) }));
    }
    // The cart-permalink form: "123:1,456:2". The Astro pages link that way,
    // so the scaffold can carry a design's buy links across unchanged.
    return text.split(",").map((pair) => {
      const [id, qty] = pair.split(":");
      return { id: Number(id), quantity: Number(qty || 1) };
    });
  };

  /* ---------- state ------------------------------------------------------ */
  let cart = config.cart || null;
  let busy = false;
  let lastError = "";

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  const refresh = () => request("cart.js").then((c) => { cart = c; render(); return c; });

  const run = (work) => enqueue(async () => {
    busy = true; lastError = ""; render();
    try {
      await work();
      const c = await refresh();
      emit("lp:cart:change", { cart: c, tier: tierState(c) });
      return c;
    } catch (error) {
      lastError = error.message;
      emit("lp:cart:error", { message: error.message, status: error.status });
      // The cart may have changed partway; show what it really is.
      await refresh().catch(() => {});
      throw error;
    } finally {
      busy = false; render();
    }
  });

  const api = {
    get: () => cart,
    refresh: () => enqueue(refresh),
    add: (items, { mode = "add", discount = null } = {}) => run(async () => {
      if (mode === "replace") await request("cart/clear.js", {});
      if (items.length) await request("cart/add.js", { items });
      if (discount) await request("cart/update.js", { discount });
    }),
    change: (key, quantity) => run(() => request("cart/change.js", { id: String(key), quantity })),
    // Relative, and resolved inside the queue. A +/- tap computed from the cart
    // as it was when tapped loses the second of two quick taps: both read
    // quantity 2 and both ask for 1. Reading the cart when the request actually
    // runs means every tap counts.
    adjust: (key, delta) => run(async () => {
      const item = cart && cart.items.find((i) => i.key === key);
      if (!item) return;
      await request("cart/change.js", { id: String(key), quantity: Math.max(0, item.quantity + delta) });
    }),
    // Absolute quantity for the one line `match` finds, looked up inside the
    // queue so it sees every earlier write: changes that line, or adds `item`
    // when there is none. For lines a variant id cannot name - the same variant
    // twice, once paid and once as a gift (properties: { _gift }).
    setLine: (match, quantity, item) => run(async () => {
      const line = cart && cart.items.find(match);
      if (line) await request("cart/change.js", { id: line.key, quantity });
      else if (quantity > 0) await request("cart/add.js", { items: [{ ...item, quantity }] });
    }),
    discount: (code) => run(() => request("cart/update.js", { discount: code })),
    // The part of the cart's discount that falls on these variants: their own
    // line discounts plus their share of cart-level (order) discounts, split by
    // line value as checkout does (it may round a cent differently). A page lists
    // only its own lines; cart.total_discount also holds other pages' discounts,
    // and capped at this page's subtotal it showed a 29,95 € bottle as free.
    discountOn: (variantIds) => {
      if (!cart) return 0;
      const ids = new Set([...variantIds].map(Number));
      const mine = cart.items.filter((i) => ids.has(i.variant_id));
      const line = mine.reduce((a, i) => a + i.original_line_price - i.final_line_price, 0);
      const order = (cart.cart_level_discount_applications || []).reduce((a, d) => a + d.total_allocated_amount, 0);
      const value = mine.reduce((a, i) => a + i.final_line_price, 0);
      return Math.round(line + (cart.items_subtotal_price ? order * value / cart.items_subtotal_price : 0));
    },
    open: () => openDrawer(),
    close: () => closeDrawer(),
    formatMoney: (cents) => money(cents),
    setMoneyFormat: (sample) => { money = formatterFromSample(sample) || money; render(); },
    tier: () => tierState(cart),
  };
  window.LPCart = api;

  // Back from checkout, Safari restores the page from its back-forward cache
  // exactly as it was left, with the cart as it was before checkout. Re-read
  // it and tell the page.
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    api.refresh().then((c) => emit("lp:cart:change", { cart: c, tier: tierState(c) })).catch(() => {});
  });

  /* ---------- rendering --------------------------------------------------
   * In place, never rebuilt. The HU offer page rebuilt its drawer on every
   * change, which replayed the entry animation, reset the scroll and dropped
   * focus in the middle of a quantity tap. Lines are keyed by the cart's own
   * line key and reused. */
  const all = (sel, scope = document) => [...scope.querySelectorAll(sel)];
  const setText = (el, text) => { if (el.textContent !== text) el.textContent = text; };
  const show = (el, on) => { el.hidden = !on; };

  // The cart hands back the full-size product image. A drawer shows it at 50-80px,
  // so ask Shopify's CDN for twice the width the design gives it; serving the
  // original there cost 300 kB the first time the HU page's drawer opened.
  const sized = (src, img) => {
    if (!src) return "";
    const w = Number(img.getAttribute("width"));
    if (!w) return src;
    const u = new URL(src, location.href);
    u.searchParams.set("width", String(w * 2));
    return u.href;
  };

  const cartField = {
    count: (c) => String(c.item_count),
    subtotal: (c) => money(c.items_subtotal_price),
    total: (c) => money(c.total_price),
    original: (c) => money(c.original_total_price),
    discount: (c) => money(c.original_total_price - c.total_price),
  };

  const lineField = {
    title: (i) => i.product_title,
    variant: (i) => (i.variant_title && i.variant_title !== "Default Title" ? i.variant_title : ""),
    qty: (i) => String(i.quantity),
    price: (i) => money(i.final_line_price),
    compare: (i) => money(i.original_line_price),
    unit: (i) => money(i.final_price),
    discounts: (i) => (i.line_level_discount_allocations || [])
      .map((d) => `${d.discount_application.title} (−${money(d.amount)})`).join(", "),
  };

  const renderLines = (container) => {
    const tpl = container.querySelector("template");
    if (!tpl) return;
    const items = (cart && cart.items) || [];
    const existing = new Map(all(":scope > [data-lp-key]", container).map((el) => [el.dataset.lpKey, el]));
    let anchor = tpl;
    for (const item of items) {
      let el = existing.get(item.key);
      if (el) existing.delete(item.key);
      else {
        el = tpl.content.firstElementChild.cloneNode(true);
        el.dataset.lpKey = item.key;
      }
      for (const f of all("[data-lp-line]", el)) setText(f, lineField[f.dataset.lpLine]?.(item) ?? "");
      for (const img of all("[data-lp-line-img]", el)) {
        const src = sized(item.image, img);
        if (src && img.getAttribute("src") !== src) img.setAttribute("src", src);
        if (!img.getAttribute("alt")) img.setAttribute("alt", item.product_title || "");
      }
      for (const s of all("[data-lp-line-show]", el)) {
        if (s.dataset.lpLineShow === "discounted") show(s, item.original_line_price > item.final_line_price);
      }
      for (const b of all("[data-lp-action]", el)) b.dataset.lpLineKey = item.key;
      if (anchor.nextElementSibling !== el) anchor.after(el);
      anchor = el;
    }
    for (const stale of existing.values()) stale.remove();
  };

  const renderDiscounts = (container) => {
    const tpl = container.querySelector("template");
    if (!tpl) return;
    for (const old of all(":scope > [data-lp-discount-row]", container)) old.remove();
    let anchor = tpl;
    for (const d of (cart && cart.cart_level_discount_applications) || []) {
      const el = tpl.content.firstElementChild.cloneNode(true);
      el.setAttribute("data-lp-discount-row", "");
      for (const f of all("[data-lp-discount-field]", el)) {
        setText(f, f.dataset.lpDiscountField === "amount" ? money(d.total_allocated_amount) : d.title);
      }
      anchor.after(el);
      anchor = el;
    }
  };

  function render() {
    if (!cart) return;
    const tier = tierState(cart);
    const discounted = cart.total_price < cart.original_total_price;
    const flags = {
      empty: cart.item_count === 0,
      "not-empty": cart.item_count > 0,
      discounted,
      "tier-reached": !!(tier && !tier.next),
      "tier-pending": !!(tier && tier.next),
      busy,
      error: !!lastError,
    };
    for (const el of all("[data-lp-cart]")) setText(el, cartField[el.dataset.lpCart]?.(cart) ?? "");
    for (const el of all("[data-lp-show]")) show(el, !!flags[el.dataset.lpShow]);
    for (const el of all("[data-lp-error]")) setText(el, lastError);
    for (const el of all("[data-lp-lines]")) renderLines(el);
    for (const el of all("[data-lp-discounts]")) renderDiscounts(el);
    for (const el of all("[data-lp-checkout]")) el.setAttribute("href", url("checkout"));
    if (tier) {
      for (const el of all("[data-lp-tier]")) {
        const k = el.dataset.lpTier;
        const text = k === "remaining"
          ? (tier.remainingIsCount ? String(tier.remaining) : money(tier.remaining))
          : k === "next" ? (tier.next ? tier.next.label || "" : "")
          : k === "reached" ? (tier.current ? tier.current.label || "" : "")
          : "";
        setText(el, text);
      }
      for (const el of all("[data-lp-tier-progress]")) {
        el.style.setProperty("--lp-progress", String(tier.progress));
        el.setAttribute("aria-valuenow", String(Math.round(tier.progress * 100)));
      }
    }
    for (const el of all("[data-lp-drawer]")) el.toggleAttribute("aria-busy", busy);
  }

  /* ---------- drawer ----------------------------------------------------- */
  let returnFocus = null;
  const drawers = () => all("[data-lp-drawer]");

  function openDrawer() {
    const list = drawers();
    if (!list.length) return;
    returnFocus = document.activeElement;
    for (const d of list) { d.hidden = false; d.setAttribute("data-open", ""); }
    document.documentElement.style.overflow = "hidden";
    const target = document.querySelector("[data-lp-dialog]") || list[0];
    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
    emit("lp:drawer:open", {});
  }

  function closeDrawer() {
    const list = drawers().filter((d) => !d.hidden);
    if (!list.length) return;
    for (const d of list) { d.hidden = true; d.removeAttribute("data-open"); }
    document.documentElement.style.overflow = "";
    if (returnFocus && returnFocus.focus) returnFocus.focus({ preventScroll: true });
    emit("lp:drawer:close", {});
  }

  /* ---------- events -----------------------------------------------------
   * Capture phase, and the event stops here: an app that delegates on
   * document for "add to cart" must not also open its own drawer. */
  const find = (event, attr) => event.composedPath().find((n) => n instanceof Element && n.hasAttribute(attr));

  document.addEventListener("click", (event) => {
    const add = find(event, "data-lp-add");
    if (add) {
      event.preventDefault();
      event.stopPropagation();
      if (add.getAttribute("aria-busy") === "true") return;
      let items;
      try { items = parseItems(add.getAttribute("data-lp-add")); } catch {
        lastError = "This button is misconfigured."; render(); return;
      }
      add.setAttribute("aria-busy", "true");
      const opens = add.getAttribute("data-lp-open") !== "false";
      api.add(items, { mode: add.getAttribute("data-lp-mode") || "add", discount: add.getAttribute("data-lp-discount") })
        .then(() => { if (opens) openDrawer(); })
        .catch(() => { if (opens) openDrawer(); })
        .finally(() => add.removeAttribute("aria-busy"));
      return;
    }
    if (find(event, "data-lp-open-drawer")) { event.preventDefault(); openDrawer(); return; }
    const action = find(event, "data-lp-action");
    if (action) {
      event.preventDefault();
      const key = action.dataset.lpLineKey;
      const k = action.dataset.lpAction;
      const work = k === "remove" ? api.change(key, 0) : api.adjust(key, k === "inc" ? 1 : -1);
      work.catch(() => {});
      return;
    }
    if (find(event, "data-lp-close")) { event.preventDefault(); closeDrawer(); }
  }, true);

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeDrawer();
  });

  // The page ships the cart it was rendered with, so nothing is fetched on load
  // and nothing competes with the LCP. Only a page without it asks.
  const start = () => {
    for (const d of drawers()) if (!d.hasAttribute("data-open")) d.hidden = true;
    if (cart) render();
    else refresh().catch(() => {});
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
})();
